import { useMemo, useState } from 'react'
import { dateVerrou } from '../api/cloture'
import {
  controlerEncaissement,
  estMobileMoney,
  etatEncaissement,
  fraisMobileMoney,
  jourBamako,
  libelleMoyenEncaissement,
  MOYENS_ENCAISSEMENT,
  peutEncaisser,
  soldeCourse,
  tauxEncaissement,
} from '../api/encaissement'
import { annulerEncaissement, enregistrerEncaissement } from '../api/operations'
import type { Client, Course, DonneesOps, MoyenEncaissement } from '../api/types'
import { useDroits } from './Contexte'
import { dateCourte, fcfa, heure } from './Format'
import { Icone } from './Icone'
import { ListeDeroulante } from './ListeDeroulante'
import { Modale } from './Modale'

/**
 * « Encaissement reçu » — enregistrer le paiement d'une course livrée à un particulier.
 *
 * ⚠️ C'est CE geste qui fait entrer l'argent dans les livres (D 551 | 571 | 521 / C 411). La livraison
 * constate la créance, jamais la trésorerie. Le montant restant dû et les frais Wave / Orange Money sont
 * RECALCULÉS par la base : l'aperçu des frais ci-dessous est indicatif.
 */
export function ModaleEncaissement({
  course,
  client,
  donnees,
  du,
  surFermer,
  surFait,
}: {
  course: Course
  client: Client | undefined
  donnees: DonneesOps
  du: number
  surFermer: () => void
  surFait: (message: string) => Promise<void>
}) {
  const aujourdhui = jourBamako()
  const [moyen, setMoyen] = useState<MoyenEncaissement>('especes')
  const [montant, setMontant] = useState(String(du))
  const [date, setDate] = useState(aujourdhui)
  const [note, setNote] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState('')
  const verrou = useMemo(() => dateVerrou(donnees.clotures, donnees.evenementsCloture), [donnees.clotures, donnees.evenementsCloture])
  const livreeLe = (course.delivered_at ?? course.created_at).slice(0, 10)

  const montantNum = Number(montant.replace(/[\s  ]/g, ''))
  const taux = tauxEncaissement(donnees.tarifs, moyen)
  const frais = estMobileMoney(moyen) && Number.isInteger(montantNum) ? fraisMobileMoney(montantNum, taux) : 0

  async function enregistrer() {
    const motifs = controlerEncaissement({ moyen, montant: montantNum, date, du, livreeLe: course.delivered_at ?? course.created_at }, aujourdhui, verrou)
    if (motifs.length) return setErreur(motifs.join(' '))
    setErreur('')
    setEnCours(true)
    try {
      const r = await enregistrerEncaissement(course.id, { moyen, montant: montantNum, date, note })
      await surFait(
        `Encaissement enregistré : ${fcfa(montantNum)} (${libelleMoyenEncaissement(moyen)}) sur ${course.ref}` +
          (r.fraisFcfa > 0 ? ` — frais mobile money ${fcfa(r.fraisFcfa)}` : '') +
          (r.resteDu > 0 ? ` — reste dû ${fcfa(r.resteDu)}` : ''),
      )
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <Modale titre={`Encaissement reçu — ${course.ref}`} ouverte surFermer={surFermer} surEnregistrer={() => void enregistrer()} libelleEnregistrer="Enregistrer l’encaissement" enCours={enCours}>
      <p className="t-muted" style={{ fontSize: 12.5, margin: '0 0 10px' }}>
        {client?.name ?? 'Client'} · course de {fcfa(course.price_fcfa)} livrée le {dateCourte(course.delivered_at)} · <b style={{ color: 'var(--ink)' }}>reste à encaisser {fcfa(du)}</b>
      </p>
      <div className="field">
        <label>Moyen de paiement</label>
        <ListeDeroulante valeur={moyen} options={MOYENS_ENCAISSEMENT.map((m) => ({ valeur: m.valeur, libelle: m.libelle }))} surChangement={(v) => setMoyen(v as MoyenEncaissement)} pleineLargeur />
      </div>
      <div className="grid-2" style={{ gap: 12 }}>
        <div className="field">
          <label htmlFor="enc-montant">Montant reçu (F)</label>
          <input id="enc-montant" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="enc-date">Date du paiement</label>
          <input id="enc-date" type="date" value={date} max={aujourdhui} min={livreeLe} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="enc-note">Note (facultatif)</label>
        <input id="enc-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex. référence de la transaction Wave" maxLength={300} />
      </div>
      <p className="t-muted" style={{ fontSize: 12 }}>
        L’écriture passera par {moyen === 'especes' ? 'la caisse (571)' : moyen === 'autre' ? 'la banque (521)' : 'le compte de monnaie électronique (551)'}, en règlement de la créance du client (411).
        {estMobileMoney(moyen) &&
          (taux > 0
            ? ` Frais ${libelleMoyenEncaissement(moyen)} (${(taux * 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %) : environ ${fcfa(frais)}, comptabilisés en charge (631) — le montant définitif est calculé par la base.`
            : ' Aucun taux de frais n’est renseigné pour ce moyen (Réglages) : aucun frais ne sera comptabilisé.')}
      </p>
      {erreur && (
        <p role="alert" style={{ color: 'var(--err)', fontSize: 12.5, fontWeight: 600 }}>
          {erreur}
        </p>
      )}
    </Modale>
  )
}

/**
 * La section « Encaissement » d'une course livrée : état, paiements enregistrés (annulables par le propriétaire et le
 * comptable) et bouton « Encaissement reçu » — seulement quand la base l'accepterait.
 */
export function SectionEncaissement({
  course,
  donnees,
  surRafraichir,
  surNotifier,
}: {
  course: Course
  donnees: DonneesOps
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
}) {
  const droits = useDroits()
  const [ouvert, setOuvert] = useState(false)
  const client = donnees.tousClients.find((c) => c.id === course.client_account_id) ?? donnees.clients.find((c) => c.id === course.client_account_id)
  const paiements = donnees.encaissements.filter((p) => p.course_id === course.id)
  const etat = etatEncaissement(course, donnees.encaissements)
  const possibilite = peutEncaisser(course, client, donnees.billed, donnees.encaissements)
  const { paye } = soldeCourse(course, donnees.encaissements)

  if (course.statut !== 'livree') return null
  if (client && client.type !== 'particulier') {
    return (
      <div className="t-muted" style={{ marginBottom: 14, fontSize: 12.5 }}>
        Course d’une PME : elle se règle par facture (forfait mensuel ou facture à l’unité), pas par encaissement de course.
      </div>
    )
  }

  async function annuler(id: string) {
    const motif = window.prompt('Annuler cet encaissement ? Il sortira des livres. Motif :', 'Saisi par erreur')
    if (motif === null) return
    try {
      await annulerEncaissement(id, motif)
      surNotifier('Encaissement annulé')
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  return (
    <>
      <h4 style={{ margin: '4px 0 8px' }}>Encaissement</h4>
      <div style={{ marginBottom: 14 }}>
        {!donnees.synchro.encaissements && (
          <div className="bandeau alerte" style={{ marginBottom: 8 }}>
            Les encaissements sont illisibles : la mise à jour de la base (migration 0033) n’est pas appliquée.
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className={`badge ${etat.pastille}`}>{etat.libelle}</span>
          <span className="mono">
            {fcfa(paye)} / {fcfa(course.price_fcfa)}
          </span>
          {possibilite.ok && droits.peut('encaissements') && !droits.apercu && (
            <button className="btn btn-green btn-sm" onClick={() => setOuvert(true)}>
              <Icone nom="wallet" /> Encaissement reçu
            </button>
          )}
        </div>
        {!possibilite.ok && etat.libelle !== 'Encaissée' && (
          <div className="t-muted" style={{ fontSize: 12, marginTop: 6 }}>
            {possibilite.raison}
          </div>
        )}
        {paiements.length > 0 && (
          <ul className="liste-lignes" style={{ marginTop: 10 }}>
            {paiements.map((p) => (
              <li key={p.id} style={p.status === 'succeeded' ? undefined : { opacity: 0.55, textDecoration: 'line-through' }}>
                <span>
                  <b>{fcfa(p.amount_fcfa)}</b> · {libelleMoyenEncaissement(p.moyen)} · {dateCourte(p.paid_at)} {heure(p.paid_at)}
                  {p.frais_fcfa > 0 && <span className="t-muted"> · frais {fcfa(p.frais_fcfa)}</span>}
                  {p.note && <span className="t-muted"> · {p.note}</span>}
                  {p.status !== 'succeeded' && <span className="t-muted"> · annulé</span>}
                </span>
                {p.status === 'succeeded' && droits.peut('annuler_encaissements') && !droits.apercu && (
                  <button className="btn btn-outline btn-sm" onClick={() => void annuler(p.id)} aria-label="Annuler cet encaissement" title="Annuler">
                    <Icone nom="x" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {ouvert && possibilite.ok && (
        <ModaleEncaissement
          course={course}
          client={client}
          donnees={donnees}
          du={possibilite.du}
          surFermer={() => setOuvert(false)}
          surFait={async (message) => {
            setOuvert(false)
            surNotifier(message)
            await surRafraichir()
          }}
        />
      )}
    </>
  )
}
